const DAY_MS = 86_400_000;
const STEP_MS = 6 * 60 * 60 * 1000;

/**
 * Noon UTC on 1 Sep 2026. The chart has no history before the presale month,
 * and noon keeps the first label on 1 Sep in timezones either side of UTC.
 */
export const INCPT_CHART_START_MS = Date.UTC(2026, 8, 1, 12);

/** Hermite smoothstep — ease in/out from 0 → 1. */
function smoothstep(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

/**
 * Deterministic INCPT path from 1 Sep 2026 through `now`. Strictly
 * non-decreasing climb to `marketPrice` with light upward jaggedness — never
 * ticks down. The last point equals `marketPrice`.
 */
export function buildIncptPriceHistory(
  marketPrice: number,
  now = Date.now(),
): [number, number][] {
  const start = Math.min(INCPT_CHART_START_MS, now - 2 * DAY_MS);
  const span = Math.max(now - start, STEP_MS);
  const N = Math.max(2, Math.round(span / STEP_MS) + 1);

  const unit = (i: number) => {
    let s = (0x6d2b79f5 ^ Math.imul(i + 1, 0x9e3779b9)) >>> 0;
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
  };

  const raw: number[] = [];
  let floor = 0.78;
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    // Slow start, then a clear lift into "now" — same silhouette as a bull All chart.
    const target = 0.78 + 0.22 * smoothstep(t);
    // Non-negative micro-wiggle only; each point ratchets at or above the last.
    const noise = unit(i) * 0.004;
    floor = Math.max(floor, target + noise);
    raw.push(floor);
  }

  const end = raw[raw.length - 1]!;
  const scale = end > 0 && marketPrice > 0 ? marketPrice / end : 1;
  const step = span / (N - 1);
  return raw.map((p, i) => [start + i * step, p * scale] as [number, number]);
}
